import * as React from 'react'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import Icon from './Icon'
import { InputProps } from './InputTypes'
import { getAppMode } from '../utils/comUtil'

const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
    const {
        onClear,
        helpId = '', //도움말이 있는 경우 input과 연결을 위해 추가
        helpMsg, //도움말 텍스트 추가
        result = 'hidden', // 기본은 숨김처리 결과값에 따라 노출
        resultId = '', //에러 or 성공 문구가 필요한 경우 input과 연결을 위해 추가
        resultMsg, //에러 or 성공 문구 텍스트 추가
        valueCount, //value 카운팅
        optionLeftElement, //좌측 컨텐츠 추가시
        optionRightElement, //우측 컨텐츠 추가시
        className = '', //className은 상위 컨테이너에서 사용된다.
        labelBig, //큰 라벨 유무
        labelBigMsg, //큰 라벨 텍스트
        //Native Prop은 따로 분리한다.
        ...rest
    } = props

    const {
        //내부적으로 native props와 중복 이름을 사용하는 props을 분리한다.
        value,
        onBlur,
        onFocus,
        onChange,
        type = 'text',
        id, //label이 있는경우 필수 추가, 그외에는 필요에 따라 사용
        label, //label이 필요한 경우 텍스트 추가
    } = rest

    const inputRef = React.useRef<HTMLInputElement>(null)
    React.useImperativeHandle(ref, () => inputRef.current, [inputRef.current])
    const [clearButton, setClearButton] = React.useState(false)

    let valueCountCheck = null
    if (valueCount) {
        valueCountCheck = (
            <div className="input-text-count">
                <p id={helpId} className="p-msg-help">
                    {helpMsg}
                </p>
                <strong className="total">
                    <span className="count">{value.length}</span>
                    &nbsp;/&nbsp;{rest.maxLength ?? 'N'}자
                </strong>
            </div>
        )
    }

    let helpMsgCheck = null
    if (!(result !== 'hidden' && resultMsg) && !valueCount && helpMsg) {
        helpMsgCheck = (
            <p id={helpId} className="p-msg-help">
                {helpMsg}
            </p>
        )
    }

    const handleClear = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        inputRef.current?.focus()
        onClear?.(e)
        setClearButton(false)
    }

    let resultCheck = null
    if (result !== 'hidden' && resultMsg) {
        resultCheck = (
            <p id={resultId} className={`p-msg-${result}`}>
                {resultMsg}
            </p>
        )
    }

    const focusHandle = (e: React.FocusEvent<HTMLInputElement, Element>) => {
        inputRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        })
        onFocus?.(e)
        if (e.target.value !== '' && onClear) setClearButton(true)
    }

    const blurHandle = (e: React.FocusEvent<HTMLInputElement, Element>) => {
        setTimeout(() => {
            setClearButton(false)
        }, 200)
        onBlur?.(e)
    }

    const changeHandle = (e) => {
        if (e.target.value !== '' && onClear) setClearButton(true)
        onChange?.(e)
    }

    React.useEffect(() => {
        if (rest.disabled) {
            setClearButton(false)
        }
    }, [rest.disabled])

    return (
        <div
            className={`input-basic ${className} ${
                result === 'error' ? '-error' : ''
            }`.trim()}
        >
            {label && <label htmlFor={id}>{label}</label>}
            {labelBig && (
                <div className="inputs-labels">
                    <label className="h6 bold" htmlFor={id}>
                        {labelBig}
                    </label>
                    {labelBigMsg && <p className="h-msg2">{labelBigMsg}</p>}
                </div>
            )}
            <div className="input">
                {optionLeftElement && (
                    <div className="option-left">{optionLeftElement}</div>
                )}
                <InputText
                    {...rest}
                    {...{ type }}
                    ref={inputRef}
                    aria-describedby={`${helpId} ${resultId}`.trim()}
                    onFocus={focusHandle}
                    onBlur={blurHandle}
                    onChange={changeHandle}
                />
                <div className="option-right">
                    <Button
                        className="btn-del"
                        onClick={handleClear}
                        style={{ display: clearButton ? 'flex' : 'none' }}
                    >
                        <Icon name="x" text="입력 내용 지우기"></Icon>
                    </Button>
                    {result === 'success' && <Icon name="check"></Icon>}
                    {optionRightElement}
                </div>
            </div>
            {helpMsgCheck}
            {valueCountCheck}
            {resultCheck}
        </div>
    )
})

export default Input
